import React from 'react';
import PropTypes from 'prop-types';
import { Mutation, Query } from 'react-apollo';
import gql from 'graphql-tag';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apolloClient from '../../lib/apolloClient';
import {
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import VisibilityIcon from '@material-ui/icons/Visibility';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import MergeFieldPicker from './MergeFieldPicker';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  form: {
    '& > *': {
      marginBottom: theme.spacing(2),
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
}));

const CREATE_TEMPLATE_MUTATION = gql`
  mutation CreateEmailTemplate(
    $name: String!
    $subject: String!
    $bodyHtml: String
    $title: String
    $subTitle: String
    $headerText: String
    $buttonText: String
    $buttonUrl: String
    $category: String!
    $layoutName: String
    $locked: Boolean
    $templateTagList: [String!]
  ) {
    create_email_template(
      name: $name
      subject: $subject
      bodyHtml: $bodyHtml
      title: $title
      subTitle: $subTitle
      headerText: $headerText
      buttonText: $buttonText
      buttonUrl: $buttonUrl
      category: $category
      layoutName: $layoutName
      locked: $locked
      templateTagList: $templateTagList
    ) {
      emailTemplate {
        id
        name
      }
      errors
    }
  }
`;

const UPDATE_TEMPLATE_MUTATION = gql`
  mutation UpdateEmailTemplate(
    $id: ID!
    $name: String
    $subject: String
    $bodyHtml: String
    $title: String
    $subTitle: String
    $headerText: String
    $buttonText: String
    $buttonUrl: String
    $category: String
    $layoutName: String
    $locked: Boolean
    $templateTagList: [String!]
  ) {
    update_email_template(
      id: $id
      name: $name
      subject: $subject
      bodyHtml: $bodyHtml
      title: $title
      subTitle: $subTitle
      headerText: $headerText
      buttonText: $buttonText
      buttonUrl: $buttonUrl
      category: $category
      layoutName: $layoutName
      locked: $locked
      templateTagList: $templateTagList
    ) {
      emailTemplate {
        id
        name
      }
      errors
    }
  }
`;

const PREVIEW_TEMPLATE_MUTATION = gql`
  mutation PreviewEmailTemplate(
    $subject: String
    $bodyHtml: String
    $title: String
    $subTitle: String
    $headerText: String
    $buttonText: String
    $buttonUrl: String
    $layoutName: String
  ) {
    preview_email_template(
      subject: $subject
      bodyHtml: $bodyHtml
      title: $title
      subTitle: $subTitle
      headerText: $headerText
      buttonText: $buttonText
      buttonUrl: $buttonUrl
      layoutName: $layoutName
    ) {
      renderedSubject
      renderedBody
      renderedTitle
      renderedSubTitle
      renderedHeaderText
      renderedButtonText
      renderedButtonUrl
      errors
    }
  }
`;

const SEND_TEST_EMAIL_MUTATION = gql`
  mutation SendTestEmail(
    $subject: String!
    $bodyHtml: String!
    $title: String
    $subTitle: String
    $headerText: String
    $buttonText: String
    $buttonUrl: String
    $layoutName: String
    $userId: ID!
    $recipientEmails: [String!]!
  ) {
    send_test_email(
      subject: $subject
      bodyHtml: $bodyHtml
      title: $title
      subTitle: $subTitle
      headerText: $headerText
      buttonText: $buttonText
      buttonUrl: $buttonUrl
      layoutName: $layoutName
      userId: $userId
      recipientEmails: $recipientEmails
    ) {
      sentCount
      errors
    }
  }
`;

const UPLOAD_IMAGE_MUTATION = gql`
  mutation UploadEmailTemplateImage($file: File!) {
    upload_email_template_image(file: $file) {
      url
      errors
    }
  }
`;

const SEARCH_USERS_QUERY = gql`
  query SearchUsers($name: String) {
    getInventoryUsers(nameSearch: $name, offset: 0, limit: 20) {
      id
      name
      email
      user {
        id
        name
        email
      }
    }
  }
`;

class TemplateBuilder extends React.Component {
  constructor(props) {
    super(props);

    this.quillRef = React.createRef();
    this.handleImageInsert = this.handleImageInsert.bind(this);
    this.quillModules = {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: this.handleImageInsert,
        },
      },
    };

    const { template } = props;
    this.state = {
      name: template?.name || '',
      subject: template?.subject || '',
      bodyHtml: template?.bodyHtml || '',
      title: template?.title || '',
      subTitle: template?.subTitle || '',
      headerText: template?.headerText || '',
      buttonText: template?.buttonText || '',
      buttonUrl: template?.buttonUrl || '',
      category: template?.category || 'general',
      layoutName: template?.layoutName || 'demo_email_layout',
      locked: template?.locked || false,
      templateTagList: template?.template_tag_list || [],
      previewOpen: false,
      previewHtml: '',
      previewSubject: '',
      testEmailOpen: false,
      testEmailRecipients: '',
      testEmailUser: null,
      testEmailUserSearch: '',
      imageUploading: false,
    };
  }

  handleImageInsert() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
    input.click();

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      this.setState({ imageUploading: true });

      try {
        const result = await apolloClient.mutate({
          mutation: UPLOAD_IMAGE_MUTATION,
          variables: { file: [file] },
          context: { hasUpload: true },
        });

        const { url, errors } = result.data.upload_email_template_image;

        if (errors && errors.length > 0) {
          alert(`Image upload failed: ${errors.join(', ')}`);
          return;
        }

        if (url && this.quillRef.current) {
          const quill = this.quillRef.current.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range ? range.index : 0, 'image', url);
          if (range) quill.setSelection(range.index + 1);
        }
      } catch (e) {
        alert(`Image upload failed: ${e.message}`);
      } finally {
        this.setState({ imageUploading: false });
      }
    };
  }

  handleChange = (field) => (event) => {
    this.setState({ [field]: event.target.value });
  };

  handleQuillChange = (value) => {
    this.setState({ bodyHtml: value });
  };

  handleMergeFieldInsert = (field) => {
    const { bodyHtml } = this.state;
    const mergeField = `{{${field}}}`;
    // Insert at cursor or append to end
    this.setState({ bodyHtml: bodyHtml + ' ' + mergeField + ' ' });
  };

  handleBack = () => {
    window.location.href = '/admin/email_templates';
  };

  handlePreviewOpen = (previewMutation) => {
    const { subject, bodyHtml, title, subTitle, headerText, buttonText, buttonUrl, layoutName } = this.state;
    
    previewMutation({
      variables: {
        subject,
        bodyHtml,
        title,
        subTitle,
        headerText,
        buttonText,
        buttonUrl,
        layoutName,
      },
    });
  };

  handlePreviewClose = () => {
    this.setState({ previewOpen: false });
  };

  handleTestEmailOpen = () => {
    this.setState({ testEmailOpen: true });
  };

  handleTestEmailClose = () => {
    this.setState({ 
      testEmailOpen: false, 
      testEmailRecipients: '', 
      testEmailUser: null,
      testEmailUserSearch: '',
    });
  };

  handleSendTestEmail = (sendTestMutation) => {
    const { subject, bodyHtml, title, subTitle, headerText, buttonText, buttonUrl, layoutName, testEmailRecipients, testEmailUser } = this.state;

    if (!testEmailRecipients.trim()) {
      alert('Please enter at least one recipient email address');
      return;
    }

    if (!testEmailUser) {
      alert('Please select a user for merge field data');
      return;
    }

    const recipientEmails = testEmailRecipients
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    sendTestMutation({
      variables: {
        subject,
        bodyHtml,
        title,
        subTitle,
        headerText,
        buttonText,
        buttonUrl,
        layoutName,
        userId: testEmailUser.user?.id || testEmailUser.id,
        recipientEmails,
      },
    });
  };

  render() {
    const { template, mergeFields = [], emailLayouts = [] } = this.props;
    const { 
      name, subject, bodyHtml, title, subTitle, headerText, buttonText, buttonUrl, category, layoutName,
      templateTagList,
      previewOpen, previewHtml, previewSubject,
      testEmailOpen, testEmailRecipients, testEmailUser, testEmailUserSearch,
      imageUploading,
    } = this.state;
    const isEditing = !!template;

    const mutation = isEditing ? UPDATE_TEMPLATE_MUTATION : CREATE_TEMPLATE_MUTATION;

    return (
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Typography variant="h4">
            {isEditing ? 'Edit Template' : 'Create Template'}
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={this.handleBack}
          >
            Back to Templates
          </Button>
        </div>

        <Paper style={{ padding: '24px' }}>
          <Mutation
            mutation={mutation}
            onCompleted={(data) => {
              // Note: mutation names use snake_case due to camelize: false
              const result = data.create_email_template || data.update_email_template;
              if (result.errors && result.errors.length > 0) {
                alert(`Errors: ${result.errors.join(', ')}`);
              } else {
                alert('Template saved successfully!');
                this.handleBack();
              }
            }}
            onError={(error) => {
              alert(`Error: ${error.message}`);
            }}
          >
            {(saveTemplate, { loading }) => (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const variables = isEditing
                    ? {
                        id: template.id,
                        name,
                        subject,
                        bodyHtml,
                        title,
                        subTitle,
                        headerText,
                        buttonText,
                        buttonUrl,
                        category,
                        layoutName,
                        templateTagList,
                      }
                    : {
                        name,
                        subject,
                        bodyHtml,
                        title,
                        subTitle,
                        headerText,
                        buttonText,
                        buttonUrl,
                        category,
                        layoutName,
                        locked: false,
                        templateTagList,
                      };
                  saveTemplate({ variables });
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      value={name}
                      onChange={this.handleChange('name')}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={category}
                        onChange={this.handleChange('category')}
                        label="Category"
                      >
                        <MenuItem value="onboarding">Onboarding</MenuItem>
                        <MenuItem value="succession">Succession</MenuItem>
                        <MenuItem value="continuity">Continuity</MenuItem>
                        <MenuItem value="marketplace">Marketplace</MenuItem>
                        <MenuItem value="vix">VIX</MenuItem>
                        <MenuItem value="general">General</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Layout</InputLabel>
                      <Select
                        value={layoutName}
                        onChange={this.handleChange('layoutName')}
                        label="Layout"
                      >
                        {emailLayouts.map((layout) => (
                          <MenuItem key={layout} value={layout}>
                            {layout}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      autoSelect
                      options={[]}
                      value={templateTagList}
                      onChange={(_e, newValue) => this.setState({ templateTagList: newValue })}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Tags"
                          placeholder="Type a tag and press Enter"
                          helperText="Add tags to help organize and find this template"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject Line"
                      value={subject}
                      onChange={this.handleChange('subject')}
                      required
                      variant="outlined"
                      helperText="Available merge fields: {{user.name}}, {{user.email}}, {{user.city}}, {{user.province}}, {{user.annual_revenue}}"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={title}
                      onChange={this.handleChange('title')}
                      variant="outlined"
                      helperText="Main email title (displayed at top of email)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subtitle"
                      value={subTitle}
                      onChange={this.handleChange('subTitle')}
                      variant="outlined"
                      helperText="Secondary title/tagline"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Header Text"
                      value={headerText}
                      onChange={this.handleChange('headerText')}
                      variant="outlined"
                      helperText="Text that appears in the header section (before the button)"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Button Text"
                      value={buttonText}
                      onChange={this.handleChange('buttonText')}
                      variant="outlined"
                      helperText="Text displayed on the call-to-action button"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Button URL"
                      value={buttonUrl}
                      onChange={this.handleChange('buttonUrl')}
                      variant="outlined"
                      helperText="Where the button links to"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Email Body
                        </Typography>
                        <MergeFieldPicker onInsert={this.handleMergeFieldInsert} />
                      </Box>
                      <ReactQuill
                        ref={this.quillRef}
                        theme="snow"
                        value={bodyHtml}
                        onChange={this.handleQuillChange}
                        modules={this.quillModules}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet',
                          'color', 'background',
                          'align',
                          'link', 'image',
                        ]}
                        style={{
                          backgroundColor: 'white',
                          minHeight: '400px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                      {imageUploading && (
                        <Typography variant="caption" color="primary" style={{ marginTop: '4px', display: 'block' }}>
                          Uploading image...
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                        Use the "Insert Merge Field" button above to add personalized fields like user name, email, etc.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <div style={{ display: 'flex', gap: '16px', justifyContent: 'space-between', marginTop: '24px' }}>
                  <Mutation
                    mutation={PREVIEW_TEMPLATE_MUTATION}
                    onCompleted={(data) => {
                      const result = data.preview_email_template;
                      if (result.errors && result.errors.length > 0) {
                        alert(`Preview errors: ${result.errors.join(', ')}`);
                      } else {
                        this.setState({
                          previewOpen: true,
                          previewHtml: result.renderedBody || '',
                          previewSubject: result.renderedSubject || '',
                        });
                      }
                    }}
                    onError={(error) => {
                      alert(`Preview error: ${error.message}`);
                    }}
                  >
                    {(previewMutation, { loading: previewing }) => (
                      <Button
                        variant="outlined"
                        startIcon={previewing ? <CircularProgress size={20} /> : <VisibilityIcon />}
                        onClick={() => this.handlePreviewOpen(previewMutation)}
                        disabled={previewing}
                      >
                        {previewing ? 'Generating Preview...' : 'Preview'}
                      </Button>
                    )}
                  </Mutation>

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={this.handleTestEmailOpen}
                  >
                    Send Test Email
                  </Button>

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Button onClick={this.handleBack}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Template'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </Mutation>
        </Paper>

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={this.handlePreviewClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Email Preview
            <IconButton
              aria-label="close"
              onClick={this.handlePreviewClose}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Subject:
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {previewSubject}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Email Body:
              </Typography>
              <Paper variant="outlined" style={{ padding: '16px', backgroundColor: '#f5f5f5' }}>
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handlePreviewClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Test Email Dialog */}
        <Dialog
          open={testEmailOpen}
          onClose={this.handleTestEmailClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Send Test Email
            <IconButton
              aria-label="close"
              onClick={this.handleTestEmailClose}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                This will send a test email with [TEST] prefix in the subject line.
              </Typography>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Recipient Email Addresses *
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="email1@example.com, email2@example.com"
                helperText="Enter comma-separated email addresses"
                value={testEmailRecipients}
                onChange={(e) => this.setState({ testEmailRecipients: e.target.value })}
                multiline
                rows={2}
              />
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                User for Merge Fields *
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Select a user whose data will be used to populate merge fields (like name, email, revenue, etc.)
              </Typography>
              <Query
                query={SEARCH_USERS_QUERY}
                variables={{ name: testEmailUserSearch }}
                skip={!testEmailOpen}
              >
                {({ loading, error, data }) => {
                  const users = data?.getInventoryUsers || [];
                  const options = users.map(iu => ({
                    ...iu,
                    label: `${iu.name} (${iu.email})`,
                  }));

                  return (
                    <Autocomplete
                      options={options}
                      getOptionLabel={(option) => option.label || ''}
                      value={testEmailUser}
                      onChange={(event, newValue) => {
                        this.setState({ testEmailUser: newValue });
                      }}
                      onInputChange={(event, newInputValue) => {
                        this.setState({ testEmailUserSearch: newInputValue });
                      }}
                      loading={loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Search for a user..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  );
                }}
              </Query>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleTestEmailClose}>
              Cancel
            </Button>
            <Mutation
              mutation={SEND_TEST_EMAIL_MUTATION}
              onCompleted={(data) => {
                const result = data.send_test_email;
                if (result.errors && result.errors.length > 0) {
                  alert(`Errors: ${result.errors.join(', ')}`);
                } else {
                  alert(`Successfully sent ${result.sentCount} test email(s)!`);
                  this.handleTestEmailClose();
                }
              }}
              onError={(error) => {
                alert(`Error: ${error.message}`);
              }}
            >
              {(sendTestMutation, { loading: sending }) => (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={() => this.handleSendTestEmail(sendTestMutation)}
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send Test Email'}
                </Button>
              )}
            </Mutation>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

TemplateBuilder.propTypes = {
  template: PropTypes.object,
  mergeFields: PropTypes.array,
  emailLayouts: PropTypes.array,
};

export default TemplateBuilder;
